import React, { useState } from "react";
import { Link } from "react-router-dom";
import { submitTalkToUs } from "../services/contact.service";

const TalkToUsPage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [topic, setTopic] = useState("General question");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(e.currentTarget);

    const payload = {
      fullName: (formData.get("fullName") as string)?.trim() || "",
      email: (formData.get("email") as string)?.trim() || "",
      company: ((formData.get("company") as string) || "").trim() || undefined,
      topic: (formData.get("topic") as string)?.trim() || "General question",
      message: (formData.get("message") as string)?.trim() || "",
      website: (formData.get("website") as string) || "", // honeypot
    };

    try {
      await submitTalkToUs(payload);
      setSubmitted(true);
      setTopic("General question");
      e.currentTarget.reset();
    } catch (err) {
      console.error("cta_talk_to_us_submit_failed", err);
      setError("Something went wrong. Please try again or email sales@pureaum.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold" style={{ color: "#0A2A4A" }}>
            Pureaum
          </Link>
          <Link to="/" className="text-sm font-semibold hover:underline" style={{ color: "#0A2A4A" }}>
            Back to homepage
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-10">
        <section className="space-y-4">
          <h1 className="text-4xl font-bold" style={{ color: "#0A2A4A" }}>
            Talk to Us
          </h1>
          <p className="text-sm max-w-2xl" style={{ color: "#2B2B2B" }}>
            Questions about product authenticity, QR flows, or how Pureaum fits your business? Send us a note.
          </p>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-3">
            <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
              What we can help with
            </h2>
            <ul className="list-disc list-inside text-sm space-y-2" style={{ color: "#2B2B2B" }}>
              <li>Anti-counterfeit strategy and real-world attack patterns (label copying, reuse)</li>
              <li>QR/NFC implementation options (outer/inner codes, packaging hierarchy)</li>
              <li>Pilot planning (timeline, operational steps, team roles)</li>
              <li>Integration questions (API, webhooks, dashboards)</li>
              <li>Data &amp; privacy approach (what is collected, how it is used)</li>
              <li>Pricing direction (high-level guidance, not detailed quotes)</li>
              <li>Partnership inquiries (integrators, packaging, distributors)</li>
              <li>Support escalation (if you are already a customer)</li>
            </ul>
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-sm" style={{ color: "#2B2B2B" }}>
              Your details are used only to respond to your request. We do not sell contact data.
            </div>
          </div>

          <div className="lg:col-span-2">
            {submitted ? (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4">
                Thanks - received. We typically respond within 1 business day.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-4 shadow-sm">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
                    {error}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="text-sm font-semibold" style={{ color: "#0A2A4A" }}>
                    Full Name *
                    <input name="fullName" required className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
                  </label>
                  <label className="text-sm font-semibold" style={{ color: "#0A2A4A" }}>
                    Email *
                    <input
                      name="email"
                      type="email"
                      required
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </label>
                </div>
                <label className="text-sm font-semibold block" style={{ color: "#0A2A4A" }}>
                  Company / Organization (optional)
                  <input name="company" className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
                </label>
                <label className="text-sm font-semibold block" style={{ color: "#0A2A4A" }}>
                  Topic *
                  <select
                    name="topic"
                    required
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {[
                      "General question",
                      "Implementation / integration",
                      "Pilot / trial",
                      "Partnership",
                      "Support",
                    ].map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-semibold block" style={{ color: "#0A2A4A" }}>
                  Message *
                  <textarea
                    name="message"
                    required
                    rows={4}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </label>
                <label className="flex items-center text-sm" style={{ color: "#2B2B2B" }}>
                  <input type="checkbox" required className="mr-2" /> I am okay with Pureaum contacting me about my inquiry.
                </label>
                <p className="text-xs" style={{ color: "#2B2B2B" }}>
                  We typically respond within 1 business day.
                </p>
                <input type="text" name="website" className="hidden" tabIndex={-1} aria-hidden="true" />
                <button
                  type="submit"
                  className="px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                  style={{ backgroundColor: "#FF7A29", color: "white", opacity: loading ? 0.7 : 1 }}
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
                <div className="text-sm">
                  Prefer email?{" "}
                  <a href="mailto:sales@pureaum.com" style={{ color: "#0A2A4A" }} className="font-semibold hover:underline">
                    sales@pureaum.com
                  </a>
                </div>
                <div className="text-sm">
                  <Link to="/contact-sales" style={{ color: "#0A2A4A" }} className="font-semibold hover:underline">
                    Prefer a sales conversation?
                  </Link>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default TalkToUsPage;
